import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import Modalheader from '../../../components/header';
import { useEffect, useState } from 'react';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import ApiDatabase from '../../../../../server';
import Button from '../../../../button';
import { toast } from 'react-toastify';

const STATUS_LABEL = {
  paid: 'Payé',
  pending: 'En attente',
  partially_paid: 'Partiellement payé',
  late: 'En retard',
  cancelled: 'Annulé',
  draft: 'Brouillon',
};

const ModalInvoicePushPennyLane = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn, language } = UseContext();
  const skippedLabel = (count) =>
    language
      .getValue('pennylanePush.skippedCount', '{count} déjà sur PennyLane')
      .replace('{count}', () => String(count));
  const token = localStorage.getItem('xsrfToken');

  const year = modalData?.year;
  const month = modalData?.month;

  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPushing, setIsPushing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!year || !month) return;
    setIsLoading(true);
    ApiDatabase.getInvoicesNotOnPennyLane(
      { token, year, month },
      (data) => {
        const list = data.invoices || [];
        setInvoices(list);
        setSelectedIds(list.map((inv) => inv.invoice_number));
        setIsLoading(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
        setIsLoading(false);
      }
    );
  }, [year, month]);

  const allSelected =
    invoices.length > 0 && invoices.every((inv) => selectedIds.includes(inv.invoice_number));

  const handleToggleAll = () => {
    setSelectedIds(allSelected ? [] : invoices.map((inv) => inv.invoice_number));
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handlePush = () => {
    if (selectedIds.length === 0) return;
    setIsPushing(true);

    ApiDatabase.pushInvoicesToPennyLane(
      { token, invoice_numbers: selectedIds },
      (data) => {
        setIsPushing(false);
        setResults(data);
        if (data.created > 0) {
          toast.success(`${data.created} facture(s) envoyée(s) sur PennyLane.`);
        }
        if (data.failed > 0) {
          toast.warning(`${data.failed} facture(s) en erreur.`);
        }
        if (data.skipped > 0) toast.info(skippedLabel(data.skipped));
        setModalDataReturn(Date.now());
      },
      (err) => {
        setIsPushing(false);
        toast.error(err.response?.data?.error || 'Une erreur est survenue.');
      }
    );
  };

  const totalAmount = invoices
    .filter((inv) => selectedIds.includes(inv.invoice_number))
    .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Exporter vers PennyLane
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4 text-sm">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : results ? (
            <div className="flex flex-col gap-3">
              <div className="flex gap-4">
                <div className="border rounded py-2 px-3 bg-green-50 text-green-800">
                  {results.created} créée(s)
                </div>
                {results.failed > 0 && (
                  <div className="border rounded py-2 px-3 bg-red-50 text-red-800">
                    {results.failed} erreur(s)
                  </div>
                )}
                {results.skipped > 0 && (
                  <div className="border rounded py-2 px-3 bg-gray-50 text-gray-700">
                    {skippedLabel(results.skipped)}
                  </div>
                )}
              </div>
              {results.details?.length > 0 && (
                <div className="max-h-[400px] overflow-y-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">N° Facture</th>
                        <th className="p-2 text-left">Entreprise</th>
                        <th className="p-2 text-left">Résultat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.details.map((d) => (
                        <tr key={d.invoice_number} className="border-t border-gray-100">
                          <td className="p-2">{d.invoice_number}</td>
                          <td className="p-2">{d.company_name || '-'}</td>
                          <td className="p-2">
                            {d.status === 'success' ? (
                              <span className="text-green-700">Créée</span>
                            ) : d.status === 'skipped' ? (
                              <span className="text-gray-500">
                                {language.getValue('pennylanePush.alreadyOnPennylane', 'Déjà sur PennyLane')}
                              </span>
                            ) : (
                              <span className="text-red-600 text-xs">{d.error}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              Toutes les factures de {month}/{year} sont déjà sur PennyLane.
            </div>
          ) : (
            <>
              <div>
                <strong>{invoices.length}</strong> facture(s) non présente(s) sur PennyLane &mdash;{' '}
                <strong>{selectedIds.length}</strong> sélectionnée(s)
                {selectedIds.length > 0 && (
                  <span className="ml-2 text-gray-500">
                    ({totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)
                  </span>
                )}
              </div>
              <div className="max-h-[400px] overflow-y-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">
                        <input type="checkbox" checked={allSelected} onChange={handleToggleAll} />
                      </th>
                      <th className="p-2 text-left">N° Facture</th>
                      <th className="p-2 text-left">Entreprise</th>
                      <th className="p-2 text-left">Statut</th>
                      <th className="p-2 text-right">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr
                        key={inv.invoice_number}
                        className="border-t border-gray-100 hover:bg-gray-50"
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(inv.invoice_number)}
                            onChange={() => handleToggleOne(inv.invoice_number)}
                          />
                        </td>
                        <td className="p-2">{inv.invoice_number}</td>
                        <td className="p-2">{inv.company_name}</td>
                        <td className="p-2">
                          <span className="text-xs text-gray-500">
                            {STATUS_LABEL[inv.payment_status] || '-'}
                          </span>
                        </td>
                        <td className="p-2 text-right">
                          {inv.amount
                            ? `${Number(inv.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {results ? (
          <Button onClick={toggle} size="LG" color="PRIMARY">Fermer</Button>
        ) : isPushing ? (
          <div className="flex gap-2">
            <Button size="LG" color="DISABLED">Annuler</Button>
            <Button size="LG" color="DISABLED">Envoi en cours ...</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button onClick={toggle} size="LG" color="SECONDARY">Annuler</Button>
            <Button
              onClick={handlePush}
              size="LG"
              disabled={selectedIds.length === 0 || isLoading}
            >
              Envoyer vers PennyLane ({selectedIds.length})
            </Button>
          </div>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalInvoicePushPennyLane;
